import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { fetchProductByTitle, addLineToSharedCart, money } from "@/lib/shopify";

export function useShopifyProduct(title, fallbackPrice) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchProductByTitle(title)
      .then((p) => mounted && setProduct(p))
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false));
    return () => (mounted = false);
  }, [title]);

  const variantId = product?.firstVariant?.id;

  const buyNow = useCallback(async (quantity = qty) => {
    if (!variantId) {
      document.getElementById("buy")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setBuying(true);
    try {
      const cart = await addLineToSharedCart(variantId, quantity);
      if (!cart?.checkoutUrl) throw new Error("Checkout URL not returned by Shopify.");
      window.location.assign(cart.checkoutUrl);
    } catch (e) {
      toast.error(e.message || "Unable to start checkout. Please try again.");
      setBuying(false);
    }
  }, [variantId, qty]);

  const addToCart = useCallback(async () => {
    if (!variantId) return;
    setBuying(true);
    try {
      const cart = await addLineToSharedCart(variantId, qty);
      toast.success(`Added ${qty} × ${product.title.split(" - ")[0]} to your cart`, {
        action: { label: "Checkout", onClick: () => window.location.assign(cart.checkoutUrl) },
      });
    } catch (e) {
      toast.error(e.message || "Unable to add to cart.");
    } finally {
      setBuying(false);
    }
  }, [variantId, qty, product]);

  const price = product ? Number(product.price.amount) : fallbackPrice;
  const available = product ? product.availableForSale && (product.firstVariant?.availableForSale ?? true) : true;

  return {
    product, loading, error, qty, setQty, buying, buyNow, addToCart,
    price, available,
    priceLabel: money(price, product?.currency || "INR"),
    totalLabel: money(price * qty, product?.currency || "INR"),
  };
}
